import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { ShopifyProduct, ShopifyProductsResponse, ShopifyAllProductsResponse, ShopifyBundle, PRODUCT_MAPPINGS } from '@/types/shopify';
import { Equipo, Mascarilla } from '@/context/AppContext';
import bundleMappings from '@/data/bundle-mappings.json';

// Environment variables (to be added by user)
const SHOPIFY_DOMAIN = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN;
const SHOPIFY_ACCESS_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
const SHOPIFY_API_VERSION = import.meta.env.VITE_SHOPIFY_API_VERSION || '2024-01';

// Create Shopify client
const client = createStorefrontApiClient({
  storeDomain: SHOPIFY_DOMAIN,
  apiVersion: SHOPIFY_API_VERSION,
  publicAccessToken: SHOPIFY_ACCESS_TOKEN,
});

// GraphQL query to fetch products by handles
const PRODUCTS_QUERY = `
  query getProducts($handles: [String!]!) {
    products(first: 20, query: $handles) {
      edges {
        node {
          id
          handle
          title
          description
          productType
          tags
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
        }
      }
    }
  }
`;

// GraphQL query to fetch ALL products from the store
const ALL_PRODUCTS_QUERY = `
  query getAllProducts($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          handle
          title
          description
          productType
          tags
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// Fallback static data (same as current hardcoded data)
const FALLBACK_EQUIPOS: Equipo[] = [
  {
    marca: 'Yuwell',
    modelo: 'YH-680B',
    precio: 14500,
    conectividad: 'Excelente',
    garantia: '1 año',
    caracteristicas: ['CPAP automático', 'EPR básico', 'Confiable y efectivo'],
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YH680B_1.webp'
  },
  {
    marca: 'ResMed',
    modelo: 'AirSense 10',
    precio: 18600,
    conectividad: 'Excelente',
    garantia: '1 año',
    caracteristicas: ['APAP automático', 'EPR avanzado', 'Muy silencioso'],
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/Airsense_20S10_20AutoSet.webp'
  },
];

const FALLBACK_MASCARILLAS: Mascarilla[] = [
  {
    marca: 'Yuwell',
    modelo: 'YN-03',
    precio: 1000,
    tipo: 'Nasal',
    caracteristicas: ['Liviana y práctica', 'Cómoda', 'Fácil limpieza'],
    compatibilidad: ['YH-680B', 'AirSense 10'],
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YN-03%20(Nasal).webp'
  },
  {
    marca: 'Yuwell',
    modelo: 'YF-02',
    precio: 1650,
    tipo: 'Oronasal',
    caracteristicas: ['Precio accesible', 'Buen sellado', 'Versátil'],
    compatibilidad: ['YH-680B', 'AirSense 10'],
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/YF-02%20(Oronasal).webp'
  },
  {
    marca: 'ResMed',
    modelo: 'AirFit N20',
    precio: 2400,
    tipo: 'Nasal',
    caracteristicas: ['Sellado suave', 'Ajuste rápido', 'Diseño minimalista'],
    compatibilidad: ['AirSense 10', 'YH-680B'],
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/N20%20(Nasal)%20Edit.jpg'
  },
  {
    marca: 'ResMed',
    modelo: 'AirFit F20',
    precio: 3200,
    tipo: 'Oronasal',
    caracteristicas: ['Cobertura completa', 'Sellado estable', 'Respiración boca/nariz'],
    compatibilidad: ['AirSense 10', 'YH-680B'],
    imagen: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/F20%20(Oronasal)%20Edit.jpg'
  },
];

// Helper function to convert Shopify product to our internal format
const convertShopifyProductToEquipo = (product: ShopifyProduct): Equipo | null => {
  const mapping = PRODUCT_MAPPINGS.find(m => m.shopifyHandle === product.handle && m.type === 'equipo');
  if (!mapping) return null;

  const fallback = FALLBACK_EQUIPOS.find(e => 
    mapping.internalId.includes(e.marca.toLowerCase()) && 
    mapping.internalId.includes(e.modelo.toLowerCase().replace(/\s+/g, '-'))
  );

  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  const imageUrl = product.images.edges[0]?.node.url || fallback?.imagen || '';

  return {
    marca: fallback?.marca || (product.title.includes('ResMed') ? 'ResMed' : 'Yuwell'),
    modelo: fallback?.modelo || product.title,
    precio: Math.round(price), // Convert to integer pesos
    conectividad: fallback?.conectividad || 'Excelente',
    garantia: fallback?.garantia || '1 año',
    caracteristicas: fallback?.caracteristicas || ['Características por confirmar'],
    imagen: imageUrl
  };
};

const convertShopifyProductToMascarilla = (product: ShopifyProduct): Mascarilla | null => {
  const mapping = PRODUCT_MAPPINGS.find(m => m.shopifyHandle === product.handle && m.type === 'mascarilla');
  if (!mapping) return null;

  const fallback = FALLBACK_MASCARILLAS.find(m => 
    mapping.internalId.includes(m.marca.toLowerCase()) && 
    mapping.internalId.includes(m.modelo.toLowerCase().replace(/\s+/g, '-'))
  );

  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  const imageUrl = product.images.edges[0]?.node.url || fallback?.imagen || '';

  return {
    marca: fallback?.marca || (product.title.includes('ResMed') ? 'ResMed' : 'Yuwell'),
    modelo: fallback?.modelo || product.title,
    precio: Math.round(price), // Convert to integer pesos
    tipo: fallback?.tipo || (product.title.toLowerCase().includes('nasal') && !product.title.toLowerCase().includes('oronasal') ? 'Nasal' : 'Oronasal'),
    caracteristicas: fallback?.caracteristicas || ['Características por confirmar'],
    compatibilidad: fallback?.compatibilidad || ['YH-680B', 'AirSense 10'],
    imagen: imageUrl
  };
};

// Main function to fetch products from Shopify
export const fetchProducts = async (): Promise<{ equipos: Equipo[], mascarillas: Mascarilla[] }> => {
  try {
    // Check if environment variables are configured
    if (!SHOPIFY_DOMAIN || !SHOPIFY_ACCESS_TOKEN) {
      console.warn('Shopify environment variables not configured, using fallback data');
      return { equipos: FALLBACK_EQUIPOS, mascarillas: FALLBACK_MASCARILLAS };
    }

    const handles = PRODUCT_MAPPINGS.map(m => `handle:${m.shopifyHandle}`).join(' OR ');
    
    const response = await client.request<ShopifyProductsResponse>(PRODUCTS_QUERY, {
      variables: { handles }
    });

    const shopifyProducts = response.data.products.edges.map(edge => edge.node);
    
    const equipos: Equipo[] = [];
    const mascarillas: Mascarilla[] = [];

    // Process each Shopify product
    shopifyProducts.forEach(product => {
      const equipoMapping = PRODUCT_MAPPINGS.find(m => m.shopifyHandle === product.handle && m.type === 'equipo');
      const mascarillaMapping = PRODUCT_MAPPINGS.find(m => m.shopifyHandle === product.handle && m.type === 'mascarilla');

      if (equipoMapping) {
        const equipo = convertShopifyProductToEquipo(product);
        if (equipo) equipos.push(equipo);
      } else if (mascarillaMapping) {
        const mascarilla = convertShopifyProductToMascarilla(product);
        if (mascarilla) mascarillas.push(mascarilla);
      }
    });

    // Fill in any missing products with fallback data
    const equipoHandles = equipos.map(e => `${e.marca.toLowerCase()}-${e.modelo.toLowerCase().replace(/\s+/g, '-')}`);
    const mascarillaHandles = mascarillas.map(m => `${m.marca.toLowerCase()}-${m.modelo.toLowerCase().replace(/\s+/g, '-')}`);

    FALLBACK_EQUIPOS.forEach(equipo => {
      const handle = `${equipo.marca.toLowerCase()}-${equipo.modelo.toLowerCase().replace(/\s+/g, '-')}`;
      if (!equipoHandles.some(eh => eh.includes(equipo.marca.toLowerCase()))) {
        equipos.push(equipo);
      }
    });

    FALLBACK_MASCARILLAS.forEach(mascarilla => {
      const handle = `${mascarilla.marca.toLowerCase()}-${mascarilla.modelo.toLowerCase().replace(/\s+/g, '-')}`;
      if (!mascarillaHandles.some(mh => mh.includes(mascarilla.marca.toLowerCase()))) {
        mascarillas.push(mascarilla);
      }
    });

    console.log(`Fetched ${equipos.length} equipos and ${mascarillas.length} mascarillas from Shopify`);
    return { equipos, mascarillas };

  } catch (error) {
    console.error('Error fetching products from Shopify:', error);
    console.warn('Falling back to static product data');
    return { equipos: FALLBACK_EQUIPOS, mascarillas: FALLBACK_MASCARILLAS };
  }
};

// Function to fetch a specific product by handle (useful for individual price updates)
export const fetchProductByHandle = async (handle: string): Promise<ShopifyProduct | null> => {
  try {
    if (!SHOPIFY_DOMAIN || !SHOPIFY_ACCESS_TOKEN) {
      return null;
    }

    const query = `
      query getProduct($handle: String!) {
        product(handle: $handle) {
          id
          handle
          title
          description
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
        }
      }
    `;

    const response = await client.request<{ product: ShopifyProduct }>(query, {
      variables: { handle }
    });

    return response.data.product;
  } catch (error) {
    console.error(`Error fetching product ${handle} from Shopify:`, error);
    return null;
  }
};

// Create fallback bundle products when Shopify API is not available
const createFallbackBundleProducts = (): ShopifyProduct[] => {
  const bundles: ShopifyProduct[] = [];
  
  bundleMappings.bundles.forEach((mapping) => {
    // Get the correct price based on equipment brand
    const bundlePrice = mapping.equipmentId === 'yuwell-yh-680b' ? 14500 : 18600;
    
    bundles.push({
      id: `gid://shopify/Product/${mapping.shopifyHandle}`,
      handle: mapping.shopifyHandle,
      title: mapping.name,
      description: `Kit completo CPAP con ${mapping.name}`,
      productType: 'Bundle',
      tags: ['kit', 'bundle', 'cpap'],
      priceRange: {
        minVariantPrice: {
          amount: bundlePrice.toString(),
          currencyCode: 'MXN'
        }
      },
      variants: {
        edges: [
          {
            node: {
              id: mapping.variants.M,
              title: 'Medium',
              price: {
                amount: bundlePrice.toString(),
                currencyCode: 'MXN'
              },
              availableForSale: true
            }
          }
        ]
      },
      images: {
        edges: [
          {
            node: {
              url: `https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/${mapping.shopifyHandle}.png`,
              altText: mapping.name
            }
          }
        ]
      }
    });
  });
  
  console.log('[SHOPIFY] Created fallback bundles:', bundles.length, bundles.map(b => ({ handle: b.handle, price: b.priceRange.minVariantPrice.amount })));
  return bundles;
};

// Function to fetch ALL products from Shopify store
export const fetchAllProducts = async (): Promise<ShopifyProduct[]> => {
  try {
    if (!SHOPIFY_DOMAIN || !SHOPIFY_ACCESS_TOKEN) {
      console.warn('[SHOPIFY] Environment variables not configured, using fallback bundle data');
      // Return fallback bundle products based on our hardcoded mappings
      return createFallbackBundleProducts();
    }

    console.log('[DEBUG] Using Shopify config:', {
      domain: SHOPIFY_DOMAIN,
      apiVersion: SHOPIFY_API_VERSION,
      hasToken: !!SHOPIFY_ACCESS_TOKEN
    });

    let allProducts: ShopifyProduct[] = [];
    let hasNextPage = true;
    let cursor: string | null = null;

    while (hasNextPage) {
      const response = await client.request<ShopifyAllProductsResponse>(ALL_PRODUCTS_QUERY, {
        variables: { 
          first: 50, // Fetch 50 products at a time
          after: cursor 
        }
      });

      console.log('[DEBUG] Raw API response:', response);
      
      if (!response.data || !response.data.products) {
        console.error('[DEBUG] Invalid API response structure:', response);
        break;
      }

      const products = response.data.products.edges.map(edge => edge.node);
      allProducts = [...allProducts, ...products];

      hasNextPage = response.data.products.pageInfo.hasNextPage;
      cursor = response.data.products.pageInfo.endCursor;
    }

    console.log(`[DEBUG] Fetched ${allProducts.length} total products from Shopify`);
    console.log('[DEBUG] Sample products:', allProducts.slice(0, 3).map(p => ({ 
      title: p.title, 
      productType: p.productType, 
      tags: p.tags 
    })));
    return allProducts;

  } catch (error) {
    console.error('Error fetching all products from Shopify:', error);
    console.error('[DEBUG] Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      error
    });
    return [];
  }
};

// Function to identify bundle products from all products
export const identifyBundles = (allProducts: ShopifyProduct[]): ShopifyBundle[] => {
  const bundles: ShopifyBundle[] = [];
  
  console.log(`[DEBUG] identifyBundles: Processing ${allProducts.length} products`);
  
  // Look for products that might be bundles based on:
  // 1. Product type contains "bundle" or "kit"
  // 2. Title contains "kit", "bundle", "combo"
  // 3. Tags include bundle-related terms
  // 4. Price range suggests it's a combination product
  
  allProducts.forEach((product, index) => {
    console.log(`[DEBUG] Product ${index + 1}:`, {
      title: product.title,
      productType: product.productType,
      tags: product.tags,
      handle: product.handle
    });
    const isBundle = 
      product.productType.toLowerCase().includes('bundle') ||
      product.productType.toLowerCase().includes('kit') ||
      product.title.toLowerCase().includes('kit') ||
      product.title.toLowerCase().includes('bundle') ||
      product.title.toLowerCase().includes('combo') ||
      product.tags.some(tag => 
        tag.toLowerCase().includes('bundle') || 
        tag.toLowerCase().includes('kit') ||
        tag.toLowerCase().includes('combo')
      );

    console.log(`[DEBUG] Product "${product.title}" isBundle:`, isBundle);

    if (isBundle) {
      // Try to identify which equipo and mascarilla this bundle contains
      let equipoComponent: string | undefined;
      let mascarillaComponent: string | undefined;

      // Check title and description for equipment names
      const titleLower = product.title.toLowerCase();
      const descriptionLower = product.description.toLowerCase();

      if (titleLower.includes('airsense') || descriptionLower.includes('airsense')) {
        equipoComponent = 'resmed-airsense-10';
      } else if (titleLower.includes('yh-680b') || titleLower.includes('yuwell') || descriptionLower.includes('yh-680b')) {
        equipoComponent = 'yuwell-yh-680b';
      }

      if (titleLower.includes('f20') || descriptionLower.includes('f20')) {
        mascarillaComponent = 'resmed-airfit-f20';
      } else if (titleLower.includes('n20') || descriptionLower.includes('n20')) {
        mascarillaComponent = 'resmed-airfit-n20';
      } else if (titleLower.includes('yf-02') || descriptionLower.includes('yf-02')) {
        mascarillaComponent = 'yuwell-yf-02';
      } else if (titleLower.includes('yn-03') || descriptionLower.includes('yn-03')) {
        mascarillaComponent = 'yuwell-yn-03';
      }

      bundles.push({
        id: product.id,
        handle: product.handle,
        title: product.title,
        description: product.description,
        price: Math.round(parseFloat(product.priceRange.minVariantPrice.amount)),
        variants: product.variants.edges.map(edge => edge.node),
        images: product.images.edges.map(edge => edge.node),
        equipoComponent,
        mascarillaComponent
      });
    }
  });

  console.log(`Identified ${bundles.length} bundle products`);
  return bundles;
};

// Function to find the best bundle match for selected equipo and mascarilla (legacy fallback)
export const findBundleForSelection = (
  bundles: ShopifyBundle[], 
  equipoMarca: string, 
  mascarillaModelo: string
): ShopifyBundle | null => {
  console.log(`[LEGACY] Attempting pattern match for ${equipoMarca} + ${mascarillaModelo}`);
  
  // Try to find exact match first
  const exactMatch = bundles.find(bundle => {
    if (!bundle.equipoComponent || !bundle.mascarillaComponent) return false;
    
    const equipoMatches = bundle.equipoComponent.includes(equipoMarca.toLowerCase());
    const mascarillaMatches = bundle.mascarillaComponent.includes(mascarillaModelo.toLowerCase().replace(/[\s-]/g, ''));
    
    return equipoMatches && mascarillaMatches;
  });

  if (exactMatch) {
    console.log(`[LEGACY] Found exact match: ${exactMatch.handle}`);
    return exactMatch;
  }

  // If no exact match, try partial matching
  const partialMatch = bundles.find(bundle => {
    const titleLower = bundle.title.toLowerCase();
    const equipoInTitle = titleLower.includes(equipoMarca.toLowerCase());
    const mascarillaInTitle = 
      titleLower.includes(mascarillaModelo.toLowerCase()) ||
      titleLower.includes(mascarillaModelo.toLowerCase().replace(/[\s-]/g, ''));
    
    return equipoInTitle && mascarillaInTitle;
  });

  if (partialMatch) {
    console.log(`[LEGACY] Found partial match: ${partialMatch.handle}`);
  } else {
    console.warn(`[LEGACY] No bundle match found for ${equipoMarca} + ${mascarillaModelo}`);
  }

  return partialMatch || null;
};